import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { useMemo, useRef } from 'react';

import { HttpClient, useHttpClient } from '@bonusly/http-client';

import { EmployeeSpotlight } from '../schemas/spotlight';

/*
 * Shared utilities
 */

enum QueryKey {
	Spotlight = 'spotlight:employee-spotlight',
	Spotlights = 'spotlight:employee-spotlights',
}

const headers = {
	'Key-Inflection': 'camel',
};

/*
 * Path and query key builders
 */

export const spotlightQueryKey = (spotlightId: string) => [QueryKey.Spotlight, spotlightId];

export const spotlightsQueryKey = (userId: string) => [QueryKey.Spotlights, userId];

export const employeeSpotlightsPath = () => 'spotlight/employee_spotlights';
export const employeeSpotlightPath = (id: string) => `spotlight/employee_spotlights/${id}`;
export const employeeSpotlightSectionPath = (spotlightId: string, sectionId: string) => `spotlight/employee_spotlights/${spotlightId}/sections/${sectionId}`;
export const sendSpotlightPath = (id: string) => `spotlight/employee_spotlights/${id}/send_spotlight`;
export const userSpotlightsListPath = (userId: string) => `spotlight/employee_spotlights?user_id=${userId}`;
export const employeeSpotlightEligibilityPath = (userId: string, startTime: string, endTime: string) =>
	`spotlight/employee_spotlights/eligibility?user_id=${userId}&start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}`;

export const lastReceivedSpotlightPath = (userId: string) => `spotlight/employee_spotlights/last_received?user_id=${userId}`;

/*
 * API Types
 */
type EmployeeSpotlightAPIData = {
	id: string;
	userId: string;
	companyId: string;
	generatedByUserId?: string | null;
	visibility?: 'private' | 'company';
	bonusIds?: string[];
	user?: {
		id: string;
		displayName: string;
		displayFirstName?: string | null;
	} | null;
	startTime: string;
	endTime: string;
	status: 'pending' | 'processing' | 'completed' | 'failed';
	totalReceivedCount?: number;
	totalGivenCount?: number;
	monthsAtCompany?: number;
	generationStartedAt?: string | null;
	generationCompletedAt?: string | null;
	generationError?: string | null;
	sections?: Array<{
		id?: string;
		sectionType?: string;
		intro?: string | null;
		title?: string | null;
		textHtml?: string | null;
		supportingEvidence?: Array<string | { bonusId?: string; source?: string; excerpt: string }>;
		strategy?: string;
		scores?: Record<string, unknown>;
	}>;
	createdAt: string;
	updatedAt: string;
	// Additional fields from full view
	data?: Record<string, unknown>;
	// Status helpers
	pending?: boolean;
	processing?: boolean;
	completed?: boolean;
	failed?: boolean;
	generationInProgress?: boolean;
	// Image and URL fields for spotlight attachment
	url: string;
	imageUrl?: string | null;
	imageHeight?: number | null;
	imageWidth?: number | null;
	thumbnailUrl?: string | null;
	thumbnailHeight?: number | null;
	thumbnailWidth?: number | null;
};

type CreateSpotlightAPIData = {
	user_id: string;
	start_time: string;
	end_time: string;
	bonus_id?: string; // Optional: auto-attach spotlight to a celebration bonus
};

type SpotlightEligibilityAPIData = {
	eligible: boolean;
	receivedMessagesCount: number;
	minimumRequired: number;
	authorized: boolean;
};

/*
 * Query Hooks
 */

// Transform API section data to match schema
const transformSection = (section: NonNullable<EmployeeSpotlightAPIData['sections']>[0]) => ({
	id: section.id || '',
	intro: section.intro || '',
	title: section.title || '',
	textHtml: section.textHtml || '',
	supportingEvidence: section.supportingEvidence,
	strategy: section.strategy,
	scores: section.scores,
	type: section.sectionType || '',
});

// Transform API response to match schema
const transformSpotlightData = (data: EmployeeSpotlightAPIData) => ({
	id: data.id,
	userId: data.userId,
	companyId: data.companyId,
	generatedByUserId: data.generatedByUserId ?? null,
	visibility: data.visibility ?? 'private',
	bonusIds: data.bonusIds ?? [],
	user: data.user ?? null,
	startTime: data.startTime,
	endTime: data.endTime,
	status: data.status,
	totalReceivedCount: data.totalReceivedCount ?? 0,
	totalGivenCount: data.totalGivenCount ?? 0,
	monthsAtCompany: data.monthsAtCompany ?? 0,
	generationStartedAt: data.generationStartedAt ?? null,
	generationCompletedAt: data.generationCompletedAt ?? null,
	generationError: data.generationError ?? null,
	sections: (data.sections || []).map(transformSection),
	data: data.data,
	createdAt: data.createdAt,
	updatedAt: data.updatedAt,
	// Image and URL fields
	url: data.url,
	imageUrl: data.imageUrl,
	imageHeight: data.imageHeight,
	imageWidth: data.imageWidth,
	thumbnailUrl: data.thumbnailUrl,
	thumbnailHeight: data.thumbnailHeight,
	thumbnailWidth: data.thumbnailWidth,
});

const createSpotlightFetcher = (http: HttpClient, spotlightId: string) => async () => {
	if (!spotlightId) {
		throw new Error('Invalid spotlight ID');
	}

	const path = employeeSpotlightPath(spotlightId);
	const res = await http.get<EmployeeSpotlightAPIData>(path, {
		headers,
	});

	return EmployeeSpotlight.parse(transformSpotlightData(res.data));
};

export const useSpotlight = (spotlightId: string, options?: { enabled?: boolean }) => {
	const http = useHttpClient();
	const isEnabled = options?.enabled !== undefined ? options.enabled : !!spotlightId;

	return useQuery({
		queryKey: spotlightQueryKey(spotlightId),
		queryFn: createSpotlightFetcher(http, spotlightId),
		enabled: isEnabled,
	});
};

const createEmployeeSpotlightsListFetcher = (http: HttpClient, userId: string) => async () => {
	const path = `${userSpotlightsListPath(userId)}`;
	const res = await http.get<EmployeeSpotlightAPIData[]>(path, {
		headers,
	});

	// Transform API response array to match schema
	return (res.data || []).map((data) => EmployeeSpotlight.parse(transformSpotlightData(data)));
};

export const useEmployeeSpotlightsForUser = (userId: string, options?: { enabled?: boolean }) => {
	const http = useHttpClient();
	const isEnabled = options?.enabled !== undefined ? options.enabled && !!userId : !!userId;
	return useQuery({
		queryKey: [...spotlightsQueryKey(userId), 'list'],
		queryFn: createEmployeeSpotlightsListFetcher(http, userId),
		enabled: isEnabled,
	});
};

export const spotlightEligibilityQueryKey = (userId: string, startTime: string, endTime: string) => [
	QueryKey.Spotlights,
	userId,
	'eligibility',
	startTime,
	endTime,
];

export type SpotlightEligibilityQueryOptions = {
	enabled?: boolean;
	staleTime?: number;
};

/**
 * Default 12-month eligibility window used by spotlight banners and menus.
 */
const useDefaultEligibilityDateRange = () =>
	useMemo(() => {
		const maxEndMonth = startOfMonth(new Date());
		const defaultEndMonth = subMonths(maxEndMonth, 1);
		const defaultStartMonth = startOfMonth(subMonths(defaultEndMonth, 11));
		return {
			startTime: startOfMonth(defaultStartMonth).toISOString(),
			endTime: endOfMonth(defaultEndMonth).toISOString(),
		};
	}, []);

/**
 * Check spotlight eligibility for a single user over the default 12-month window.
 * Shares query cache with useDirectReportsSpotlightEligibility.
 */
export const useSpotlightEligibilityDefault = (userId: string, options?: SpotlightEligibilityQueryOptions) => {
	const { startTime, endTime } = useDefaultEligibilityDateRange();
	return useSpotlightEligibility(userId, startTime, endTime, { staleTime: 5 * 60 * 1000, ...options });
};

export const useSpotlightEligibility = (userId: string, startTime: string, endTime: string, options?: SpotlightEligibilityQueryOptions) => {
	const http = useHttpClient();
	const isEnabled = options?.enabled !== undefined ? options.enabled : !!userId && !!startTime && !!endTime;

	return useQuery(
		spotlightEligibilityQueryKey(userId, startTime, endTime),
		async () => {
			const res = await http.get<SpotlightEligibilityAPIData>(employeeSpotlightEligibilityPath(userId, startTime, endTime), { headers });
			return res.data;
		},
		{
			...(options ?? {}),
			enabled: isEnabled,
		}
	);
};

/*
 * Last Received Spotlight Query
 * Returns the most recent spotlight shared with the specified user
 */
export const lastReceivedSpotlightQueryKey = (userId: string) => [QueryKey.Spotlights, userId, 'last-received'];

export const useLastReceivedSpotlight = (userId: string | undefined, options?: { enabled?: boolean }) => {
	const http = useHttpClient();
	const isEnabled = options?.enabled !== undefined ? options.enabled : !!userId;

	return useQuery({
		queryKey: lastReceivedSpotlightQueryKey(userId || ''),
		queryFn: async () => {
			if (!userId) return null;
			try {
				const res = await http.get<EmployeeSpotlightAPIData>(lastReceivedSpotlightPath(userId), { headers });
				// 204 No Content means no spotlight found
				if (!res.data) return null;
				return EmployeeSpotlight.parse(transformSpotlightData(res.data));
			} catch (error) {
				// If 204 or no data, return null
				if ((error as { response?: { status?: number } })?.response?.status === 204) {
					return null;
				}
				throw error;
			}
		},
		enabled: isEnabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

/*
 * Direct Reports Eligibility Hook
 * Batch-fetches spotlight eligibility for a list of direct reports over a rolling 12-month window.
 */
type DirectReportRef = { userId: string };

export const useDirectReportsSpotlightEligibility = (directReports: DirectReportRef[], options?: { enabled?: boolean }) => {
	const http = useHttpClient();
	const { startTime, endTime } = useDefaultEligibilityDateRange();

	const isEnabled = options?.enabled !== false;

	const eligibilityQueries = useQueries({
		queries: directReports.map((report) => ({
			queryKey: spotlightEligibilityQueryKey(report.userId, startTime, endTime),
			queryFn: async () => {
				const res = await http.get<{ eligible: boolean; receivedMessagesCount: number; minimumRequired: number }>(
					employeeSpotlightEligibilityPath(report.userId, startTime, endTime),
					{ headers }
				);
				return { userId: report.userId, ...res.data };
			},
			staleTime: 5 * 60 * 1000,
			enabled: isEnabled && directReports.length > 0,
		})),
	});

	const eligibilityMap = useMemo(() => {
		const map: Record<string, { eligible: boolean; isLoading: boolean }> = {};
		eligibilityQueries.forEach((query, index) => {
			const userId = directReports[index]?.userId;
			if (userId) {
				map[userId] = {
					eligible: query.data?.eligible ?? false,
					isLoading: query.isLoading,
				};
			}
		});
		return map;
	}, [eligibilityQueries, directReports]);

	const isLoading = eligibilityQueries.some((query) => query.isLoading);

	return { eligibilityMap, isLoading };
};

/*
 * Mutation Hooks
 */
type CreateSpotlightOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (spotlight: EmployeeSpotlight) => void;
};

export const useCreateSpotlight = (options: CreateSpotlightOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: CreateSpotlightAPIData) => {
			try {
				const res = await http.post<EmployeeSpotlightAPIData>(employeeSpotlightsPath(), data, {
					headers,
				});

				// Transform API response to match schema
				const spotlight = EmployeeSpotlight.parse(transformSpotlightData(res.data));

				// Update cache and ensure query stays active for polling
				// React Query's refetchInterval will handle polling globally
				queryClient.setQueryData(spotlightQueryKey(spotlight.id), spotlight);
				queryClient.invalidateQueries(spotlightsQueryKey(data.user_id));

				options.onSuccess?.(spotlight);
				return spotlight;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Update Spotlight Section mutation
 */
type UpdateSpotlightSectionData = {
	spotlightId: string;
	sectionId: string;
	textHtml: string;
};

type UpdateSpotlightSectionOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (spotlight: EmployeeSpotlight) => void;
};

export const useUpdateSpotlightSection = (options: UpdateSpotlightSectionOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UpdateSpotlightSectionData) => {
			try {
				const res = await http.patch<EmployeeSpotlightAPIData>(
					employeeSpotlightSectionPath(data.spotlightId, data.sectionId),
					{ text_html: data.textHtml },
					{ headers }
				);

				const spotlight = EmployeeSpotlight.parse(transformSpotlightData(res.data));
				queryClient.setQueryData(spotlightQueryKey(spotlight.id), spotlight);
				options.onSuccess?.(spotlight);
				return spotlight;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Delete Spotlight Section mutation
 */
type DeleteSpotlightSectionData = {
	spotlightId: string;
	sectionId: string;
};

type DeleteSpotlightSectionOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (spotlight: EmployeeSpotlight) => void;
};

export const useDeleteSpotlightSection = (options: DeleteSpotlightSectionOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: DeleteSpotlightSectionData) => {
			try {
				const res = await http.delete<EmployeeSpotlightAPIData>(employeeSpotlightSectionPath(data.spotlightId, data.sectionId), { headers });

				const spotlight = EmployeeSpotlight.parse(transformSpotlightData(res.data));
				queryClient.setQueryData(spotlightQueryKey(spotlight.id), spotlight);
				options.onSuccess?.(spotlight);
				return spotlight;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Send Spotlight mutation
 */
type SendSpotlightData = {
	spotlightId: string;
	recipientUserIds: string[];
};

type SendSpotlightOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: () => void;
};

export const useSendSpotlight = (options: SendSpotlightOptions = {}) => {
	const http = useHttpClient();

	// Use a ref to always access the latest callbacks, avoiding stale closure issues
	// when the component re-renders with new callback functions
	const optionsRef = useRef(options);
	optionsRef.current = options;

	return useMutation({
		mutationFn: async (data: SendSpotlightData) => {
			await http.post(sendSpotlightPath(data.spotlightId), { recipient_user_ids: data.recipientUserIds }, { headers });
		},
		onSuccess: () => optionsRef.current.onSuccess?.(),
		onError: (error) => optionsRef.current.onError?.(error),
	});
};
